import { View, Text } from "../components/base";
import { Icon } from "../components/base/Icon";
import { PageLayout } from "../components/PageLayout";
import { useAuth } from "../hooks/useAuth";

type ResourceCardProps = {
  icon: string;
  title: string;
  description: string;
  href: string;
  external?: boolean;
};

const ResourceCard = ({ icon, title, description, href, external }: ResourceCardProps) => {
  const handleClick = () => {
    if (external) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = href;
    }
  };

  return (
    <View
      onClick={handleClick}
      className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-brand dark:hover:border-brand-light cursor-pointer transition-all duration-200 hover:shadow-md"
    >
      <View className="flex items-start gap-4">
        <View className="p-3 bg-brand/10 dark:bg-brand-light/10 rounded-lg">
          <Icon
            name={icon}
            size={24}
            className="!text-brand dark:!text-brand-light"
          />
        </View>
        <View className="flex-1">
          <View className="flex items-center gap-2 mb-2">
            <Text className="text-lg font-semibold text-gray-900 dark:text-white">{title}</Text>
            {external && (
              <Icon
                name="open"
                size={16}
                className="text-gray-400"
              />
            )}
          </View>
          <Text className="text-gray-600 dark:text-gray-400">{description}</Text>
        </View>
      </View>
    </View>
  );
};

export const ResourcesPage = () => {
  const { isAdmin } = useAuth();
  const brandName = "VoiceRun";
  const supportEmail = "support@voicerun.com";

  const resources: ResourceCardProps[] = [
    {
      icon: "folder_open",
      title: "Documentation",
      description: "Learn how to build voice agents with guides, tutorials, and API references.",
      href: "https://voicerun.com/docs",
      external: true,
    },
    ...(isAdmin
      ? [
          {
            icon: "brush",
            title: "Style Guide",
            description: "Design system components and patterns for internal development.",
            href: "/style-guide",
            external: true,
          },
        ]
      : []),
  ];

  return (
    <PageLayout
      title="Resources"
      description={`Documentation, guides, and tools to help you build with ${brandName}.`}
    >
      <View className="grid grid-cols-1 gap-4 mt-8">
        {resources.map((resource) => (
          <ResourceCard
            key={resource.title}
            {...resource}
          />
        ))}
      </View>

      <View className="mt-12 p-6 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <Text className="text-center text-gray-700 dark:text-gray-300">
          Need more help? Contact support at{" "}
          <a
            href={`mailto:${supportEmail}`}
            className="text-brand dark:text-brand-light hover:underline"
          >
            {supportEmail}
          </a>
          .
        </Text>
      </View>
    </PageLayout>
  );
};
